import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Plus, X, Trash2, Send, Copy, Loader2, ExternalLink, Video } from 'lucide-react';
import Results from './Results';
import ProposalBreakdown from './ProposalBreakdown';
import Header from './Header';
import Footer from './Footer';
import { calculateDebtConsolidation, mockDebts, mockMortgage } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Tiny segmented toggle: Estimate vs Buffer (used per fee row)
const FeeKindToggle = ({ kind, onChange, testId }) => {
  const opts = [
    { key: 'estimate', label: 'Est.', full: 'Estimate' },
    { key: 'buffer', label: 'Buf.', full: 'Buffer' },
  ];
  return (
    <div className="inline-flex rounded-md border border-input bg-background p-0.5 h-9" data-testid={testId} title="Mark this row as Estimate or Buffer">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={`px-2 text-[11px] font-semibold rounded-sm transition-colors ${
            kind === o.key
              ? (o.key === 'estimate' ? 'bg-blue-600 text-white' : 'bg-amber-500 text-white')
              : 'text-gray-500 hover:text-gray-700'
          }`}
          aria-label={o.full}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
};

// ── Debt-consolidation helpers ─────────────────────────────────────────────
const DEBT_TYPES = [
  'Credit card', 'Personal loan', 'Car loan', 'BNPL (Afterpay/Zip)',
  'Store card', 'HECS/HELP', 'Tax debt', 'Other',
];

// Standard amortisation payment for a given balance / annual rate / years.
const amortisedMonthly = (balance, annualRatePct, years) => {
  const P = parseFloat(balance) || 0;
  const r = (parseFloat(annualRatePct) || 0) / 100 / 12;
  const n = (parseFloat(years) || 0) * 12;
  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
};

// Inline-saving estimate: cashflow improvement when this debt is rolled into
// the mortgage and amortised over the **remaining mortgage term**, not its
// original short term. This is how debt consolidation is actually pitched —
// total interest may rise, but weekly/monthly repayments drop dramatically
// because the same balance is spread over 25-30 yrs at a low rate vs 2-5 yrs
// at a high rate.
const computeMonthlySaving = (debt, mortgageRate, mortgageTerm) => {
  const balance = parseFloat(debt.amount) || 0;
  const debtYears = parseFloat(debt.term) || 0;
  const mYears = parseFloat(mortgageTerm) || 25;
  if (balance <= 0 || debtYears <= 0) return 0;
  const currentPay = parseFloat(debt.min_payment) > 0
    ? parseFloat(debt.min_payment)
    : amortisedMonthly(balance, debt.rate, debtYears);
  const newPay = amortisedMonthly(balance, mortgageRate, mYears);
  return Math.max(0, Math.round(currentPay - newPay));
};

// Footer that shows live totals as the broker types.
const DebtsSummaryFooter = ({ debts, mortgageRate, mortgageTerm }) => {
  const totalBalance = debts.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
  const totalMin = debts.reduce((s, d) => {
    const explicit = parseFloat(d.min_payment) || 0;
    if (explicit > 0) return s + explicit;
    return s + amortisedMonthly(d.amount, d.rate, d.term);
  }, 0);
  const weightedRate = totalBalance > 0
    ? debts.reduce((s, d) => s + (parseFloat(d.rate) || 0) * (parseFloat(d.amount) || 0), 0) / totalBalance
    : 0;
  const monthlySavings = debts.reduce((s, d) => s + computeMonthlySaving(d, mortgageRate, mortgageTerm), 0);
  // Convert monthly → fortnightly → weekly using lender-standard divisors
  // (12 months/yr ÷ 26 fortnights/yr = 0.4615…; ÷ 52 weeks/yr = 0.2308…).
  const fortnightly = Math.round(monthlySavings * 12 / 26);
  const weekly      = Math.round(monthlySavings * 12 / 52);
  const fmt = (n) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

  return (
    <div className="space-y-2" data-testid="debts-summary-footer">
      {/* Top row — pipeline totals */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
        <Stat label="Debts" value={debts.length} />
        <Stat label="Total balance" value={fmt(totalBalance)} />
        <Stat label="Avg rate" value={`${weightedRate.toFixed(1)}%`} />
        <Stat label="Combined min / mo" value={fmt(Math.round(totalMin))} />
      </div>
      {/* Bottom row — cashflow improvement (this is the pitch) */}
      <div className="grid grid-cols-3 gap-2 px-4 py-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/60" data-testid="debts-saving-cards">
        <SavingCard period="Weekly"     value={fmt(weekly)} accent={weekly > 0} />
        <SavingCard period="Fortnightly" value={fmt(fortnightly)} accent={fortnightly > 0} />
        <SavingCard period="Monthly"    value={fmt(monthlySavings)} accent={monthlySavings > 0} />
      </div>
      <p className="text-[10px] text-slate-500 dark:text-slate-400 px-2 italic">
        Estimated repayment reduction if these debts roll into the mortgage at {mortgageRate}% over {mortgageTerm} yrs.
        Total interest paid may rise — this view shows cashflow, not lifetime cost.
      </p>
    </div>
  );
};

const SavingCard = ({ period, value, accent }) => (
  <div className={`flex flex-col items-center justify-center py-2 px-2 rounded ${accent ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
    <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{period}</span>
    <span className={`text-base font-extrabold tabular-nums ${accent ? 'text-emerald-600 dark:text-emerald-300' : 'text-slate-400'}`}>
      ↓ {value}
    </span>
  </div>
);

const Stat = ({ label, value, tone = 'default' }) => {
  const toneClass = {
    default: 'text-slate-900 dark:text-white',
    good:    'text-emerald-600 dark:text-emerald-400',
    muted:   'text-slate-500',
  }[tone];
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">{label}</span>
      <span className={`text-base font-extrabold tabular-nums ${toneClass}`}>{value}</span>
    </div>
  );
};

const Calculator = () => {
  const [debts, setDebts] = useState(mockDebts);
  const [mortgage, setMortgage] = useState(mockMortgage);
  const [consolidationRate, setConsolidationRate] = useState(6.2);
  const [results, setResults] = useState(null);

  // Break Down Report — Client & Broker
  const [clientName, setClientName] = useState('');
  const [brokerName, setBrokerName] = useState('');

  // Break Down Report — Lender Options (3)
  const [lenders, setLenders] = useState([
    { name: '', rate: '', type: 'Variable', repayment: 'Principal & Interest', offset: false, redraw: false, extraPayments: false },
    { name: '', rate: '', type: 'Variable', repayment: 'Principal & Interest', offset: false, redraw: false, extraPayments: false },
    { name: '', rate: '', type: 'Variable', repayment: 'Principal & Interest', offset: false, redraw: false, extraPayments: false },
  ]);
  const [recommendedLender, setRecommendedLender] = useState(0);

  // Break Down Report — Funds Required
  const [purchasePrice, setPurchasePrice] = useState('');
  const [stampDuty, setStampDuty] = useState('');
  const [registration, setRegistration] = useState('');
  const [legalFees, setLegalFees] = useState('1500');
  const [lenderFees, setLenderFees] = useState('500');
  const [lmi, setLmi] = useState('');
  const [mandateFee, setMandateFee] = useState('');
  // Estimate vs Buffer flag per built-in fee row ('estimate' or 'buffer')
  const [feeKinds, setFeeKinds] = useState({
    purchasePrice: 'estimate',
    stampDuty: 'estimate',
    registration: 'estimate',
    legalFees: 'estimate',
    lenderFees: 'estimate',
    lmi: 'estimate',
    mandateFee: 'estimate',
  });
  // Custom additional fees
  const [customFees, setCustomFees] = useState([]); // [{ id, label, amount, kind }]
  const setFeeKind = (key, kind) => setFeeKinds((s) => ({ ...s, [key]: kind }));
  const addCustomFee = () => setCustomFees((cf) => [...cf, { id: Date.now(), label: '', amount: '', kind: 'estimate' }]);
  const removeCustomFee = (id) => setCustomFees((cf) => cf.filter((f) => f.id !== id));
  const updateCustomFee = (id, field, value) => setCustomFees((cf) => cf.map((f) => (f.id === id ? { ...f, [field]: value } : f)));

  // Break Down Report — Funds Available
  const [loanAmount, setLoanAmount] = useState('');
  const [paidDeposit, setPaidDeposit] = useState('');
  const [gift, setGift] = useState('');
  const [fhog, setFhog] = useState('');
  const [otherContributions, setOtherContributions] = useState('');

  const updateLender = (i, field, value) => {
    setLenders(lenders.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  };

  const addDebt = (preset = {}) => {
    setDebts([...debts, {
      id: Date.now() + Math.floor(Math.random() * 1000),
      name: '', amount: 0, rate: 0, term: 0,
      debt_type: '', min_payment: 0, lender: '',
      ...preset,
    }]);
  };

  // One-tap quick-adds with sensible defaults so the broker can scaffold a
  // common debt and just edit the amount/rate.
  const quickAdds = [
    { label: 'Credit card',  preset: { debt_type: 'Credit card',   name: 'Credit Card',   rate: 19.9, term: 2 } },
    { label: 'Personal loan',preset: { debt_type: 'Personal loan', name: 'Personal Loan', rate: 12.5, term: 3 } },
    { label: 'Car loan',     preset: { debt_type: 'Car loan',      name: 'Car Loan',      rate: 8.5,  term: 5 } },
    { label: 'BNPL',         preset: { debt_type: 'BNPL',          name: 'Afterpay / Zip', rate: 0,    term: 1 } },
  ];

  const removeDebt = (id) => {
    if (debts.length > 1) {
      setDebts(debts.filter(debt => debt.id !== id));
    }
  };

  const TEXT_DEBT_FIELDS = new Set(['name', 'debt_type', 'lender']);
  const updateDebt = (id, field, value) => {
    setDebts(debts.map(debt =>
      debt.id === id
        ? { ...debt, [field]: TEXT_DEBT_FIELDS.has(field) ? value : (parseFloat(value) || 0) }
        : debt
    ));
  };

  const updateMortgage = (field, value) => {
    setMortgage({ ...mortgage, [field]: parseFloat(value) || 0 });
  };

  const calculate = () => {
    const result = calculateDebtConsolidation(debts, mortgage, consolidationRate);
    setResults(result);
  };

  // ── Live Proposal flow (replaces the legacy PDF download) ─────────
  const navigate = useNavigate();
  const { token: authToken } = useAuth();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [sending, setSending] = useState(false);
  const [shareModal, setShareModal] = useState(null); // {meetingId, proposalId, clientLink, revisitLink}

  const buildConsolidationPayload = () => {
    // Strip ids — ConsolidationSlides only needs name/amount/rate/term
    const cleanDebts = (debts || []).map((d) => ({
      name: d.name || '',
      amount: Number(d.amount) || 0,
      rate: Number(d.rate) || 0,
      term: Number(d.term) || 0,
    }));
    return {
      debts: cleanDebts,
      mortgage: {
        remainingBalance: Number(mortgage?.remainingBalance) || 0,
        interestRate: Number(mortgage?.interestRate) || 0,
        remainingTerm: Number(mortgage?.remainingTerm) || 0,
      },
      consolidationRate: Number(consolidationRate) || 0,
      // Extra context preserved on the record (not rendered by current deck;
      // available for future enhancements without a schema change).
      brokerContext: {
        lenders,
        recommendedLender,
        fundsRequired: { purchasePrice, stampDuty, registration, legalFees, lenderFees, lmi, mandateFee },
        feeKinds,
        customFees,
        fundsAvailable: { loanAmount, paidDeposit, gift, fhog, otherContributions },
      },
    };
  };

  const sendLiveProposal = async () => {
    if (!authToken) {
      toast.error('You must be signed in to send a live proposal');
      return;
    }
    if (!clientName?.trim()) {
      toast.error('Please enter the client name before sending the live proposal.');
      return;
    }
    setSending(true);
    try {
      // 1. Mint meeting
      const mRes = await fetch(`${BACKEND}/api/meetings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          title: `Consolidation Proposal — ${clientName}`,
          client_name: clientName,
        }),
      });
      if (!mRes.ok) throw new Error('Could not create meeting');
      const meeting = await mRes.json();

      // 2. Mint consolidation proposal linked to that meeting
      const pRes = await fetch(`${BACKEND}/api/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          type: 'consolidation',
          title: `Consolidation Proposal — ${clientName}`,
          client_name: clientName,
          meeting_id: meeting.id,
          payload: buildConsolidationPayload(),
        }),
      });
      if (!pRes.ok) throw new Error('Could not create proposal');
      const proposal = await pRes.json();

      const clientLink = `${window.location.origin}/join/${meeting.room_id}?t=${meeting.guest_token}`;
      const revisitLink = `${window.location.origin}/p/${proposal.revisit_token}`;
      setShareModal({ meetingId: meeting.id, proposalId: proposal.id, clientLink, revisitLink });
      toast.success('Live proposal ready! Send the link below to your client.');
    } catch (e) {
      console.error('Live proposal failed:', e);
      toast.error(e.message || 'Could not create live proposal');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async (link, label = 'Link') => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(`${label} copied to clipboard`);
    } catch (e) {
      toast.info(link);
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Accelerate Consolidation Calculator
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See how much you could reduce by consolidating high-interest debts into your mortgage 
            and using a redraw account to accelerate your mortgage payoff
          </p>
        </div>

        {/* Client & Broker Details + Lender Options */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          <Card data-testid="client-broker-card">
            <CardHeader>
              <CardTitle>Client &amp; Broker Details</CardTitle>
              <CardDescription>Personalises the live proposal you'll share with your client.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Client Name</Label>
                <Input
                  data-testid="client-name-input"
                  placeholder="John & Mary Smith"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                />
              </div>
              <div>
                <Label>Broker Name (Optional)</Label>
                <Input
                  data-testid="broker-name-input"
                  placeholder="Your name"
                  value={brokerName}
                  onChange={(e) => setBrokerName(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="lender-options-card">
            <CardHeader>
              <CardTitle>Lender Options (3 Required)</CardTitle>
              <CardDescription>Tick the recommended option — it will be highlighted on the report.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {lenders.map((lender, i) => (
                <div key={i} className={`p-3 border rounded-lg ${recommendedLender === i ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm text-gray-700">Option {i + 1}</span>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="recommendedLender"
                        checked={recommendedLender === i}
                        onChange={() => setRecommendedLender(i)}
                        data-testid={`recommended-lender-${i}`}
                      />
                      <span className="text-xs font-semibold text-emerald-700">Recommended</span>
                    </label>
                  </div>
                  <Input
                    placeholder="Lender Name"
                    value={lender.name}
                    onChange={(e) => updateLender(i, 'name', e.target.value)}
                    className="mb-2"
                  />
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Rate (e.g. 6.5)"
                      value={lender.rate}
                      onChange={(e) => updateLender(i, 'rate', e.target.value)}
                    />
                    <select
                      className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                      value={lender.type}
                      onChange={(e) => updateLender(i, 'type', e.target.value)}
                    >
                      <option>Variable</option>
                      <option>Fixed</option>
                      <option>Split</option>
                    </select>
                  </div>
                  <select
                    className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm mb-2"
                    value={lender.repayment}
                    onChange={(e) => updateLender(i, 'repayment', e.target.value)}
                  >
                    <option>Principal &amp; Interest</option>
                    <option>Interest Only</option>
                  </select>
                  <div className="flex flex-wrap gap-3 text-xs">
                    {['offset', 'redraw', 'extraPayments'].map((feat) => (
                      <label key={feat} className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={lender[feat]}
                          onChange={(e) => updateLender(i, feat, e.target.checked)}
                        />
                        <span>{feat === 'extraPayments' ? 'Extra Payments' : feat[0].toUpperCase() + feat.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* (Funds Required / Funds Available section removed — this is a
            debt-consolidation calculator, not a settlement-funds tool. The
            related state vars + brokerContext payload below stay populated
            with empty strings so the backend proposal schema is unchanged.) */}

        {/* Current Debts — full width, rich table on desktop, stacked on mobile */}
        <div className="mb-8">
          <Card data-testid="current-debts-card">
            <CardHeader>
              <CardTitle>Current Debts</CardTitle>
              <CardDescription>
                Add every personal loan, credit card, car loan and high-interest debt the client wants to consolidate.
                The summary at the bottom updates live.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Quick-add buttons — one tap to scaffold a common debt type */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-widest text-text-muted mr-1">Quick add:</span>
                {quickAdds.map((qa) => (
                  <Button
                    key={qa.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addDebt(qa.preset)}
                    data-testid={`quick-add-${qa.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="h-7 text-xs"
                  >
                    <Plus className="w-3 h-3 mr-1" /> {qa.label}
                  </Button>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addDebt()}
                  data-testid="quick-add-blank"
                  className="h-7 text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> Blank
                </Button>
              </div>

              {/* Desktop table view */}
              <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
                <table className="w-full text-sm" data-testid="debts-table">
                  <thead className="bg-slate-50 dark:bg-slate-800/60 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="px-2 py-2 text-left w-36">Type</th>
                      <th className="px-2 py-2 text-left">Lender / Name</th>
                      <th className="px-2 py-2 text-right w-28">Balance</th>
                      <th className="px-2 py-2 text-right w-20">Rate %</th>
                      <th className="px-2 py-2 text-right w-20">Term (y)</th>
                      <th className="px-2 py-2 text-right w-28">Min / mo</th>
                      <th className="px-2 py-2 text-right w-44">Save / mo (over mortgage)</th>
                      <th className="px-2 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {debts.map((debt) => {
                      const saving = computeMonthlySaving(debt, mortgage.interestRate, mortgage.remainingTerm);
                      return (
                        <tr key={debt.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30" data-testid={`debt-row-${debt.id}`}>
                          <td className="px-2 py-1">
                            <select
                              className="w-full h-8 px-1.5 rounded border border-input bg-background text-xs"
                              value={debt.debt_type || ''}
                              onChange={(e) => updateDebt(debt.id, 'debt_type', e.target.value)}
                              data-testid={`debt-type-${debt.id}`}
                            >
                              <option value="">— Type —</option>
                              {DEBT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              placeholder="e.g. NAB credit card"
                              value={debt.name}
                              onChange={(e) => updateDebt(debt.id, 'name', e.target.value)}
                              className="h-8 text-sm"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number" placeholder="15000"
                              value={debt.amount || ''}
                              onChange={(e) => updateDebt(debt.id, 'amount', e.target.value)}
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number" step="0.1" placeholder="12.5"
                              value={debt.rate || ''}
                              onChange={(e) => updateDebt(debt.id, 'rate', e.target.value)}
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number" placeholder="3"
                              value={debt.term || ''}
                              onChange={(e) => updateDebt(debt.id, 'term', e.target.value)}
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number" placeholder="—"
                              value={debt.min_payment || ''}
                              onChange={(e) => updateDebt(debt.id, 'min_payment', e.target.value)}
                              className="h-8 text-sm text-right"
                            />
                          </td>
                          <td className="px-2 py-1 text-right">
                            {saving ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                ↓ ${saving.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/mo
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {debts.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removeDebt(debt.id)} className="h-7 w-7 p-0">
                                <X className="h-3.5 w-3.5 text-slate-500" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile stacked card view */}
              <div className="md:hidden space-y-3">
                {debts.map((debt, index) => {
                  const saving = computeMonthlySaving(debt, mortgage.interestRate, mortgage.remainingTerm);
                  return (
                    <div key={debt.id} className="p-3 border border-slate-200 dark:border-slate-700 rounded-lg space-y-2 bg-slate-50/40 dark:bg-slate-900/40" data-testid={`debt-card-mobile-${debt.id}`}>
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Debt {index + 1}</span>
                        {debts.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeDebt(debt.id)} className="h-7 w-7 p-0">
                            <X className="h-4 w-4 text-slate-500" />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Type</Label>
                          <select
                            className="w-full h-9 px-2 rounded border border-input bg-background text-sm"
                            value={debt.debt_type || ''}
                            onChange={(e) => updateDebt(debt.id, 'debt_type', e.target.value)}
                          >
                            <option value="">— Select —</option>
                            {DEBT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Lender / Name</Label>
                          <Input placeholder="e.g. NAB credit card" value={debt.name} onChange={(e) => updateDebt(debt.id, 'name', e.target.value)} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Balance ($)</Label>
                          <Input type="number" placeholder="15000" value={debt.amount || ''} onChange={(e) => updateDebt(debt.id, 'amount', e.target.value)} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Rate (%)</Label>
                          <Input type="number" step="0.1" placeholder="12.5" value={debt.rate || ''} onChange={(e) => updateDebt(debt.id, 'rate', e.target.value)} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Term (yrs)</Label>
                          <Input type="number" placeholder="3" value={debt.term || ''} onChange={(e) => updateDebt(debt.id, 'term', e.target.value)} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Min payment</Label>
                          <Input type="number" placeholder="—" value={debt.min_payment || ''} onChange={(e) => updateDebt(debt.id, 'min_payment', e.target.value)} className="h-9" />
                        </div>
                      </div>
                      {saving > 0 && (
                        <div className="text-center bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[11px] font-bold py-1.5 rounded">
                          Saves ${saving.toLocaleString('en-AU', { maximumFractionDigits: 0 })}/mo when rolled into the mortgage
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rolling summary footer */}
              <DebtsSummaryFooter debts={debts} mortgageRate={mortgage.interestRate} mortgageTerm={mortgage.remainingTerm} />
            </CardContent>
          </Card>
        </div>

        {/* Mortgage Details + Consolidation Rate — full width below the debts */}
        <div className="mb-8">
          {/* Mortgage Details Section */}
          <Card>
            <CardHeader>
              <CardTitle>Current Mortgage</CardTitle>
              <CardDescription>Enter your existing mortgage details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Remaining Balance ($)</Label>
                <Input 
                  type="number"
                  placeholder="350000"
                  value={mortgage.remainingBalance || ''}
                  onChange={(e) => updateMortgage('remainingBalance', e.target.value)}
                />
              </div>
              <div>
                <Label>Current Interest Rate (%)</Label>
                <Input 
                  type="number"
                  step="0.1"
                  placeholder="6.2"
                  value={mortgage.interestRate || ''}
                  onChange={(e) => updateMortgage('interestRate', e.target.value)}
                />
              </div>
              <div>
                <Label>Remaining Term (years)</Label>
                <Input 
                  type="number"
                  placeholder="25"
                  value={mortgage.remainingTerm || ''}
                  onChange={(e) => updateMortgage('remainingTerm', e.target.value)}
                />
              </div>
              <div className="pt-4 border-t">
                <Label>Consolidation Rate (%)</Label>
                <Input 
                  type="number"
                  step="0.1"
                  placeholder="6.2"
                  value={consolidationRate || ''}
                  onChange={(e) => setConsolidationRate(parseFloat(e.target.value) || 0)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  The interest rate for the consolidated mortgage (usually same as current mortgage rate)
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <Button onClick={calculate} size="lg" className="px-8" data-testid="calculate-savings-btn">
            Calculate Reduction
          </Button>
        </div>

        {results && <Results results={results} debts={debts} mortgage={mortgage} consolidationRate={consolidationRate} />}

        {results && <ProposalBreakdown results={results} debts={debts} mortgage={mortgage} consolidationRate={consolidationRate} />}

        {/* Send Live Proposal — replaces the legacy PDF download */}
        {results && (
          <div className="mt-16 pt-8 border-t-2 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              Present this proposal live to your client
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              We'll generate a secure link your client can click to join a live video meeting where you'll walk them through the consolidation strategy in real-time — no PDFs, no downloads.
            </p>
            <Button
              data-testid="send-live-proposal-btn"
              onClick={sendLiveProposal}
              disabled={sending}
              size="lg"
              className="px-12 py-6 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg disabled:opacity-60"
            >
              {sending ? (
                <><Loader2 className="h-6 w-6 mr-3 animate-spin" /> Creating live proposal…</>
              ) : (
                <><Send className="h-6 w-6 mr-3" /> Send Live Proposal</>
              )}
            </Button>
            <p className="text-xs text-gray-500 mt-3">
              You'll get a meeting link to share + a 30-day revisit link the client can re-open anytime.
            </p>
          </div>
        )}
      </div>

      {/* Share modal — appears after sendLiveProposal succeeds */}
      <Dialog open={!!shareModal} onOpenChange={(o) => !o && setShareModal(null)}>
        <DialogContent data-testid="live-proposal-share-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-emerald-500" /> Live proposal ready
            </DialogTitle>
          </DialogHeader>
          {shareModal && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-600">
                Send this secure link to your client. They click and join the live walkthrough — no login, no app.
              </p>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-1">Client meeting link</p>
                <div className="bg-slate-100 border border-slate-200 rounded-md px-3 py-2 text-xs break-all font-mono" data-testid="client-meeting-link">
                  {shareModal.clientLink}
                </div>
                <Button
                  onClick={() => copyLink(shareModal.clientLink, 'Client link')}
                  data-testid="copy-client-link-btn"
                  className="mt-2 bg-emerald-600 hover:bg-emerald-700 w-full"
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy Client Link
                </Button>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wide font-bold text-gray-500 mb-1">Revisit link (read-only)</p>
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs break-all font-mono text-slate-600" data-testid="revisit-link">
                  {shareModal.revisitLink}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">Valid for 30 days — share if the client wants to re-open the proposal later.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyLink(shareModal.revisitLink, 'Revisit link')}
                  data-testid="copy-revisit-link-btn"
                  className="mt-2 w-full"
                >
                  <Copy className="h-3.5 w-3.5 mr-2" /> Copy Revisit Link
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShareModal(null)}>Close</Button>
            {shareModal && (
              <Button
                data-testid="enter-room-from-share-btn"
                onClick={() => navigate(`/meeting/${shareModal.meetingId}`)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> Enter Meeting Room
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default Calculator;
